import { EventEmitter, Injectable, Type, inject } from '@angular/core';

import {
    BetslipOnboardingConfig,
    BetslipUpsellConfig,
    LineSwitcherConfig,
    NotificationsConfig,
    OverAskConfig,
    SportsUserSettings,
    TeaserBetConfig,
} from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory } from '@frontend/sports/common/core/feature/logging';
import { ComponentLoaderService, ComponentProxy } from '@frontend/sports/deferred/feature/component-loader';
import { ModuleLoaderService } from '@frontend/sports/deferred/feature/module-loader';
import { LazyClientConfigBase } from '@frontend/vanilla/core';

import { BetslipDigitalModule } from '../../betslip/digital/betslip-digital.module';

export interface RightColumnBinding {
    inColumn: boolean;
}

export interface EditMyBetAddPicksContainerMobileComponentBinding {
    picksContainerBottomOffset: EventEmitter<number>;
}

export interface BetslipBarComponentBindings {
    barClick: EventEmitter<void>;
    myBetsClick: EventEmitter<void>;
}

@Injectable({ providedIn: 'root' })
export class BetslipDigitalComponentLoaderService extends ComponentLoaderService<BetslipDigitalModule> {
    private sportsUserSettings = inject(SportsUserSettings);
    private betslipUpsellConfig = inject(BetslipUpsellConfig);
    private lineSwitcherConfig = inject(LineSwitcherConfig);
    private betslipOnboardingConfig = inject(BetslipOnboardingConfig);
    private overAskConfig = inject(OverAskConfig);
    private notificationsConfig = inject(NotificationsConfig);
    private teaserBetConfig = inject(TeaserBetConfig);

    moduleName = 'BetslipDigitalModule';

    constructor() {
        const moduleLoader = inject(ModuleLoaderService);
        const loggerFactory = inject(LoggerFactory);

        super(moduleLoader, loggerFactory);
    }

    getBetslipComponent(): ComponentProxy<RightColumnBinding> {
        return this.getComponentProxy((module) => module.betslipComponent, undefined, ...this.lazyLoadedConfigs);
    }

    getEditBetComponent(): ComponentProxy<RightColumnBinding> {
        return this.getComponentProxy((module) => module.editBetComponent, undefined, ...this.lazyLoadedConfigs);
    }

    getQuickBetComponent(): ComponentProxy {
        return this.getComponentProxy((module) => module.quickBetContainerComponent, undefined, ...this.lazyLoadedConfigs);
    }

    getEditBetAddPicksContainerMobileComponent(): ComponentProxy<EditMyBetAddPicksContainerMobileComponentBinding> {
        return this.getComponentProxy((module) => module.editBetAddPicksContainerMobileComponent, undefined, ...this.lazyLoadedConfigs);
    }

    getModule(): Promise<Type<BetslipDigitalModule>> {
        return import('../../betslip/digital/betslip-digital.module').then((m) => m.BetslipDigitalModule);
    }

    getRewardTokensComponent(): ComponentProxy {
        return this.getComponentProxy((module) => module.rewardTokensBannerComponent, undefined, ...this.lazyLoadedConfigs);
    }

    getBetslipReturnsMessageComponent(): ComponentProxy {
        return this.getComponentProxy((module) => module.betslipReturnsMessagesComponent, undefined, ...this.lazyLoadedConfigs);
    }

    getBetslipBarComponent(): ComponentProxy<BetslipBarComponentBindings> {
        return this.getComponentProxy((module) => module.betslipBarComponent, undefined, ...this.lazyLoadedConfigs);
    }

    private get lazyLoadedConfigs(): readonly LazyClientConfigBase[] {
        return [
            this.sportsUserSettings,
            this.betslipUpsellConfig,
            this.lineSwitcherConfig,
            this.betslipOnboardingConfig,
            this.overAskConfig,
            this.notificationsConfig,
            this.teaserBetConfig,
        ];
    }
}
